import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import CategoryServ from '../service/CategoryServ';

const CategoryContext = createContext();

// Module-level cache to ensure only ONE API call is ever in flight
// even if React Strict Mode double-mounts the Provider or if we remount.
let globalCategoryCache = null;
let categoryFetchPromise = null;
let lastFetchTime = 0;
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

export const CategoryProvider = ({ children }) => {
    const [categories, setCategories] = useState(globalCategoryCache || []);
    const [loading, setLoading] = useState(!globalCategoryCache);
    const [error, setError] = useState(null);

    const fetchCategories = useCallback(async (force = false) => {
        const now = Date.now();
        
        // Use memory cache if available and not expired
        if (!force && globalCategoryCache && (now - lastFetchTime < CACHE_TTL)) {
            if (categories.length === 0) setCategories(globalCategoryCache);
            setLoading(false);
            return;
        }

        // If a request is already in flight, reuse its promise
        if (categoryFetchPromise) {
            setLoading(true);
            try {
                const data = await categoryFetchPromise;
                setCategories(data);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
            return;
        }

        // Otherwise, initiate a new request and cache the promise
        setLoading(true);
        setError(null);
        
        categoryFetchPromise = CategoryServ.index({ limit: 'all' })
            .then(res => {
                const data = res?.data || res || [];
                globalCategoryCache = data;
                lastFetchTime = Date.now();
                setCategories(data);
                return data;
            })
            .catch(err => {
                console.error("Failed to fetch global categories:", err);
                setError(err);
                throw err;
            })
            .finally(() => {
                categoryFetchPromise = null;
                setLoading(false);
            });

        return categoryFetchPromise;
    }, [categories.length]);

    useEffect(() => {
        fetchCategories().catch(err => {
            console.error("Unhandled category fetch error:", err);
        });
    }, [fetchCategories]);

    // Background refresh trigger method (optional, for components to manually trigger a silent update)
    const refreshCategories = useCallback(() => {
        fetchCategories(true);
    }, [fetchCategories]);

    // Build expensive tree structures exactly ONCE and memoize them
    // Parent Categories: Items with no parentId
    const parentCategories = useMemo(() => {
        return categories.filter(c => !c.parentId);
    }, [categories]);

    // Sub Categories: Items with a parentId
    const subCategories = useMemo(() => {
        return categories.filter(c => !!c.parentId);
    }, [categories]);

    // Category Lookup Map for fast O(1) retrieval
    const categoryLookupMap = useMemo(() => {
        const map = new Map();
        categories.forEach(c => {
            const id = c._id || c.id;
            if (id) map.set(id, c);
        });
        return map;
    }, [categories]);

    // Full Category Tree (Optional, if needed for complex nested dropdowns)
    const categoryTree = useMemo(() => {
        const map = new Map();
        // Initialize map with parent categories
        parentCategories.forEach(parent => {
            const id = parent._id || parent.id;
            map.set(id, { ...parent, children: [] });
        });
        
        // Append subcategories to their respective parents
        subCategories.forEach(sub => {
            if (sub.parentId) {
                const parentId = typeof sub.parentId === 'object' ? (sub.parentId._id || sub.parentId.id) : sub.parentId;
                const parent = map.get(parentId);
                if (parent) {
                    parent.children.push(sub);
                }
            }
        });
        
        return Array.from(map.values());
    }, [parentCategories, subCategories]);

    const value = useMemo(() => ({
        categories,
        parentCategories,
        subCategories,
        categoryLookupMap,
        categoryTree,
        loading,
        error,
        refreshCategories
    }), [categories, parentCategories, subCategories, categoryLookupMap, categoryTree, loading, error, refreshCategories]);

    return (
        <CategoryContext.Provider value={value}>
            {children}
        </CategoryContext.Provider>
    );
};

export const useCategory = () => {
    const context = useContext(CategoryContext);
    if (context === undefined) {
        throw new Error('useCategory must be used within a CategoryProvider');
    }
    return context;
};
